/**
 * TokenSection: Zeigt die mit dem Deck verbundenen Tokens als offenen Trenner-Abschnitt
 * auf dem Playmat-Boden — kein eigener Kasten, sondern ein `ui/Divider`-Label
 * („Token-Begleitmaterial") trennt den Token-Bereich von den Kategorien darüber. Kopf:
 * `ui/SectionHeader` + Anzahl links, Export-Button rechts (öffnet das Deck-Export-Modal
 * im „Nur Tokens"-Modus). Tokens rendern über `ui/CardImage` (einseitig → kein Flip,
 * Master-Fallback/Radius).
 */
import React from 'react';
import { Upload } from 'lucide-react';
import SectionHeader from '../../../ui/SectionHeader';
import CardImage from '../../../ui/CardImage';
import Divider from '../../../ui/Divider';
import '../EditorChrome.css';

// Breite eines Token-Bildes in px (Grid-Kachelbreite).
export const TOKEN_IMAGE_WIDTH = 150;

/**
 * TokenCard – einzelnes Token-Bild über ui/CardImage. Name als Hover-Tooltip.
 */
function TokenCard({ token, width = TOKEN_IMAGE_WIDTH }) {
  return (
    <div className="de-token-card" style={{ width: `${width}px` }} title={token.name || 'Token'}>
      <CardImage src={token.imageUrl} alt={token.name || 'Token'} />
    </div>
  );
}

/**
 * Dedupliziert Tokens nach `id` — Single Source für die Anzeige-Zahl, damit
 * die Kachel und die Sprungzeile der Kommandobrücke nie auseinanderlaufen.
 */
export function countUniqueTokens(tokens) {
  const seenIds = new Set();
  let count = 0;
  for (const token of tokens || []) {
    if (token && token.id != null && !seenIds.has(token.id)) {
      seenIds.add(token.id);
      count += 1;
    }
  }
  return count;
}

/**
 * TokenSection – Kachel für alle Tokens eines Decks.
 * `onExport` (optional): öffnet das Export-Modal (Token-Modus) — der Konsument
 * setzt dabei den „Nur Tokens"-Startmodus. Fehlt es, wird kein Export-Button gezeigt.
 * Ref-fähig (`React.forwardRef`, auf dem äußeren Wrapper): die Tokens-Sprungzeile
 * der Kommandobrücke scrollt darüber per `scrollIntoView`.
 */
export const TokenSection = React.forwardRef(function TokenSection({ tokens = [], minHeight, onExport }, ref) {
  // Dedupliziere Tokens nach ID
  const uniqueTokens = [];
  const seenIds = new Set();
  for (const token of tokens || []) {
    if (token && token.id != null && !seenIds.has(token.id)) {
      seenIds.add(token.id);
      uniqueTokens.push(token);
    }
  }

  // Äußerer Wrapper trägt den minHeight-Puffer für die Stacks-Hover-Expand-Kaskade —
  // unabhängig davon, wie hoch die Kachel selbst ist (bei wenigen Tokens sonst zu kurz).
  return (
    <div ref={ref} className="de-token-section" style={minHeight ? { minHeight } : undefined}>
      {uniqueTokens.length > 0 && (
        <section className="de-token-area">
          <Divider>Token-Begleitmaterial</Divider>
          <div className="de-token-area__head">
            <SectionHeader size="sm">{`Tokens (${uniqueTokens.length})`}</SectionHeader>
            {onExport && (
              <button type="button" className="btn-secondary de-token-area__export" onClick={onExport}>
                <Upload size={16} aria-hidden /> Export
              </button>
            )}
          </div>

          <div className="de-token-area__grid">
            {uniqueTokens.map((token, idx) => (
              <TokenCard key={token.id || idx} token={token} width={TOKEN_IMAGE_WIDTH} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
});

export default TokenSection;
